<h2 mat-dialog-title class="dialog-title">
  Closed Deal
  <button mat-icon-button class="close-btn" mat-dialog-close aria-label="Close">
    <mat-icon>close</mat-icon>
  </button>
</h2>

<mat-dialog-content class="closed-deal-content">
  <mat-stepper
    linear
    orientation="horizontal"
    labelPosition="bottom"
    #stepper
    (selectionChange)="onStepChange($event)">
    <!-- Step 1: Setup -->
    <mat-step [stepControl]="setupForm" label="Setup">
      <form [formGroup]="setupForm" class="step-form">
        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Transaction Type</mat-label>
            <mat-select formControlName="transactionType">
              @for (t of transactionTypes; track t) {
                <mat-option [value]="t">{{ t }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Loan Purpose</mat-label>
            <mat-select formControlName="loanPurpose">
              @for (p of loanPurposes; track p) {
                <mat-option [value]="p">{{ p }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Deal Name</mat-label>
          <input matInput formControlName="dealName" />
        </mat-form-field>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Facility ID</mat-label>
            <input matInput formControlName="facilityId" />
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label># Lenders</mat-label>
            <input
              matInput
              type="number"
              formControlName="numLenders"
              min="1" />
          </mat-form-field>
        </div>

        <div class="step-actions">
          <button mat-flat-button matStepperNext [disabled]="setupForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- Step 2: Structure -->
    <mat-step [stepControl]="structureForm" label="Structure">
      <form [formGroup]="structureForm" class="step-form">
        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Product Type</mat-label>
            <mat-select formControlName="productType">
              @for (p of (productTypes$ | async) ?? []; track p.id) {
                <mat-option [value]="p.id">{{
                  p.description ?? p.label ?? p.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Rate Type</mat-label>
            <mat-select formControlName="rateType">
              @for (r of rateTypes; track r) {
                <mat-option [value]="r">{{ r }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="form-row form-row--3col">
          <mat-form-field appearance="outline">
            <mat-label>Amount</mat-label>
            <input matInput type="number" formControlName="amount" min="0" />
            <span matTextPrefix>$&nbsp;</span>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Term</mat-label>
            <input matInput type="number" formControlName="term" min="1" />
            <span matTextSuffix>mo</span>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Amortization</mat-label>
            <input
              matInput
              type="number"
              formControlName="amortization"
              min="0" />
            <span matTextSuffix>mo</span>
          </mat-form-field>
        </div>

        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Closed Date</mat-label>
            <input
              matInput
              [matDatepicker]="closedPicker"
              formControlName="closedDate" />
            <mat-datepicker-toggle
              matSuffix
              [for]="closedPicker"></mat-datepicker-toggle>
            <mat-datepicker #closedPicker></mat-datepicker>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Maturity Date</mat-label>
            <input
              matInput
              [matDatepicker]="maturityPicker"
              formControlName="maturityDate" />
            <mat-datepicker-toggle
              matSuffix
              [for]="maturityPicker"></mat-datepicker-toggle>
            <mat-datepicker #maturityPicker></mat-datepicker>
          </mat-form-field>
        </div>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button
            mat-flat-button
            matStepperNext
            [disabled]="structureForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- Step 3: Borrower -->
    <mat-step [stepControl]="borrowerForm" label="Borrower">
      <form [formGroup]="borrowerForm" class="step-form">
        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'GNR_INDUSTRY' | translate }}</mat-label>
            <mat-select formControlName="industry">
              @for (i of (industries$ | async) ?? []; track i.id) {
                <mat-option [value]="i.id">{{
                  i.description ?? i.label ?? i.id
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Revenue Size</mat-label>
            <mat-select formControlName="revenueSize">
              @for (r of revenueSizes; track r) {
                <mat-option [value]="r">{{ r }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        </div>

        <div class="toggle-row">
          <mat-slide-toggle formControlName="peSponsored">
            PE Sponsored
          </mat-slide-toggle>
        </div>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button
            mat-flat-button
            matStepperNext
            [disabled]="borrowerForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- Step 4: Pricing -->
    <mat-step [stepControl]="pricingForm" label="Pricing">
      <form [formGroup]="pricingForm" class="step-form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Prepayment</mat-label>
          <mat-select formControlName="prepayment">
            @for (p of prepaymentOptions; track p) {
              <mat-option [value]="p">{{ p }}</mat-option>
            }
          </mat-select>
        </mat-form-field>

        @if (!isFloatingRate()) {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Fixed Rate</mat-label>
            <input
              matInput
              type="number"
              formControlName="fixedRate"
              min="0"
              step="0.01" />
            <span matTextSuffix>%</span>
          </mat-form-field>
        }

        @if (isFloatingRate()) {
          <div class="form-row form-row--3col">
            <mat-form-field appearance="outline">
              <mat-label>Index</mat-label>
              <mat-select formControlName="index">
                @for (i of rateIndices; track i) {
                  <mat-option [value]="i">{{ i }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Spread</mat-label>
              <input
                matInput
                type="number"
                formControlName="spread"
                step="0.01" />
              <span matTextSuffix>%</span>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Floor</mat-label>
              <input
                matInput
                type="number"
                formControlName="floor"
                step="0.01" />
              <span matTextSuffix>%</span>
            </mat-form-field>
          </div>
        }

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button
            mat-flat-button
            matStepperNext
            [disabled]="pricingForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- Step 5: Collateral -->
    <mat-step [stepControl]="collateralForm" label="Collateral">
      <form [formGroup]="collateralForm" class="step-form">
        <div class="form-row form-row--2col">
          <mat-form-field appearance="outline">
            <mat-label>Collateral Type</mat-label>
            <mat-select formControlName="collateralType">
              @for (c of collateralTypes; track c) {
                <mat-option [value]="c">{{ c }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Guarantor</mat-label>
            <input matInput formControlName="guarantor" />
          </mat-form-field>
        </div>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button
            mat-flat-button
            matStepperNext
            [disabled]="collateralForm.invalid">
            {{ 'CMD_NEXT' | translate }}
            <mat-icon>arrow_forward</mat-icon>
          </button>
        </div>
      </form>
    </mat-step>

    <!-- Step 6: CRE (conditional) -->
    @if (showCreStep()) {
      <mat-step [stepControl]="creForm" label="CRE">
        <form [formGroup]="creForm" class="step-form">
          <div class="form-row form-row--2col">
            <mat-form-field appearance="outline">
              <mat-label>Property Class</mat-label>
              <mat-select formControlName="propertyClass">
                @for (c of propertyClasses; track c) {
                  <mat-option [value]="c">{{ c }}</mat-option>
                }
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Project Type</mat-label>
              <mat-select formControlName="projectType">
                @for (t of projectTypes; track t) {
                  <mat-option [value]="t">{{ t }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          </div>

          <div class="form-row form-row--3col">
            <mat-form-field appearance="outline">
              <mat-label>LTV</mat-label>
              <input
                matInput
                type="number"
                formControlName="ltv"
                min="0"
                max="100"
                step="0.1" />
              <span matTextSuffix>%</span>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>LTC</mat-label>
              <input
                matInput
                type="number"
                formControlName="ltc"
                min="0"
                max="100"
                step="0.1" />
              <span matTextSuffix>%</span>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>NOI Ratio</mat-label>
              <input
                matInput
                type="number"
                formControlName="noiRatio"
                min="0"
                step="0.01" />
              <span matTextSuffix>x</span>
            </mat-form-field>
          </div>

          <div class="step-actions">
            <button mat-stroked-button matStepperPrevious>
              <mat-icon>arrow_back</mat-icon>
              {{ 'CMD_BACK' | translate }}
            </button>
            <button mat-flat-button matStepperNext [disabled]="creForm.invalid">
              {{ 'CMD_NEXT' | translate }}
              <mat-icon>arrow_forward</mat-icon>
            </button>
          </div>
        </form>
      </mat-step>
    }

    <!-- Step 7: Covenants -->
    <mat-step label="Covenants">
      <form [formGroup]="covenantsForm" class="step-form">
        <p class="section-label">Financial Covenants</p>
        <div class="covenants-grid" [formArrayName]="'financialCovenants'">
          @for (
            opt of financialCovenantOptions;
            track opt.key;
            let i = $index
          ) {
            <mat-checkbox [formControlName]="i">{{ opt.label }}</mat-checkbox>
          }
        </div>

        <div class="form-row form-row--2col mt-16">
          <mat-form-field appearance="outline">
            <mat-label>Origination Fee</mat-label>
            <input
              matInput
              type="number"
              formControlName="originationFee"
              min="0"
              step="0.01" />
            <span matTextSuffix>%</span>
          </mat-form-field>
        </div>

        <div class="toggles-row">
          <mat-slide-toggle formControlName="hedge">Hedge</mat-slide-toggle>
          <mat-slide-toggle formControlName="performancePricing"
            >Performance Pricing</mat-slide-toggle
          >
        </div>

        <div class="step-actions">
          <button mat-stroked-button matStepperPrevious>
            <mat-icon>arrow_back</mat-icon>
            {{ 'CMD_BACK' | translate }}
          </button>
          <button
            mat-flat-button
            class="qt-dialog-primary-button"
            (click)="onSubmit()"
            [disabled]="!isFormValid() || isSaving()">
            @if (isSaving()) {
              Saving...
            } @else {
              {{ 'CMD_SAVE' | translate }}
            }
          </button>
        </div>
      </form>
    </mat-step>
  </mat-stepper>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-stroked-button (click)="onCancel()" [disabled]="isSaving()">
    {{ 'GNR_CANCEL' | translate }}
  </button>
</mat-dialog-actions>
